import { useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from '@flote/ui';
import {
  Plus,
  MapPin,
  RefreshCw,
  Loader2,
  Building2,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { deliveryTsr } from '../../api';
import { DestinationFormModal } from '../../components/destinations';
import type { CreateDestinationRequest } from '@flote/contracts/http';
import { getErrorMessage } from '../../lib/error-utils';

const PAGE_SIZE = 10;

export function DestinationsPage() {
  // Filter state
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [appliedCityFilter, setAppliedCityFilter] = useState('');
  const [appliedStateFilter, setAppliedStateFilter] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // List destinations query - fetch more than needed for client-side pagination
  const listQuery = deliveryTsr.adminDestinations.list.useQuery({
    queryKey: ['destinations', appliedCityFilter, appliedStateFilter],
    queryData: {
      query: {
        city: appliedCityFilter || undefined,
        state: appliedStateFilter || undefined,
        limit: 500,
      },
    },
  });

  const allDestinations = listQuery.data?.status === 200 ? listQuery.data.body : [];

  // Client-side pagination
  const totalPages = Math.ceil(allDestinations.length / PAGE_SIZE);
  const paginatedDestinations = allDestinations.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Mutations
  const createMutation = deliveryTsr.adminDestinations.create.useMutation();
  const syncMutation = deliveryTsr.adminDestinations.sync.useMutation();

  const handleCreate = useCallback(async (data: CreateDestinationRequest) => {
    setIsProcessing(true);

    try {
      const result = await createMutation.mutateAsync({
        body: data,
      });

      if (result.status === 201) {
        toast.success('Destination created successfully');
        setIsCreateModalOpen(false);
        listQuery.refetch();
      } else {
        toast.error('Failed to create destination');
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error(getErrorMessage(error, 'Failed to create destination'));
    } finally {
      setIsProcessing(false);
    }
  }, [createMutation, listQuery]);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await syncMutation.mutateAsync({
        body: {},
      });

      if (response.status === 200) {
        toast.success(`${response.body.synced} destinations in cache`);
        listQuery.refetch();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to sync destinations');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync destinations'));
      console.error('Sync destinations error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [syncMutation, listQuery]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedCityFilter(cityFilter);
    setAppliedStateFilter(stateFilter);
    setPage(1);
  };

  const clearFilters = () => {
    setCityFilter('');
    setStateFilter('');
    setAppliedCityFilter('');
    setAppliedStateFilter('');
    setPage(1);
  };

  const hasFilters = appliedCityFilter || appliedStateFilter;

  const formatAddress = (address: {
    name?: string;
    number: string;
    street: string;
    apartment?: string;
    city: string;
    state?: string;
    postalCode?: string;
    country: string;
  }): string => {
    const parts = [];
    if (address.number) parts.push(address.number);
    if (address.street) parts.push(address.street);
    if (address.apartment) parts.push(`Apt ${address.apartment}`);
    return parts.join(' ') || 'No street address';
  };

  const formatLocation = (address: {
    city: string;
    state?: string;
    postalCode?: string;
  }): string => {
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.join(', ') || '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Destinations</h1>
          <p className="text-sm text-muted-foreground">
            Manage delivery destinations and addresses
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing}
            className="w-full sm:w-auto"
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync Cache
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Create Destination
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={applyFilters} className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="cityFilter">City</Label>
              <Input
                id="cityFilter"
                placeholder="Filter by city..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-32 space-y-2">
              <Label htmlFor="stateFilter">State</Label>
              <Input
                id="stateFilter"
                placeholder="e.g., CA"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
                maxLength={2}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 sm:flex-none">
                Apply
              </Button>
              {hasFilters && (
                <Button type="button" variant="outline" onClick={clearFilters} className="flex-1 sm:flex-none">
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Destinations Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Destinations
              {allDestinations.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({allDestinations.length} total)
                </span>
              )}
            </CardTitle>
            {listQuery.isFetching && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : allDestinations.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-sm font-medium">No destinations found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {hasFilters
                  ? 'Try adjusting your filters or create a new destination.'
                  : 'Get started by creating your first destination.'}
              </p>
              <Button className="mt-4" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Destination
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="w-[100px]">Coordinates</TableHead>
                      <TableHead className="w-[120px]">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDestinations.map((destination) => (
                      <TableRow key={destination.id}>
                        <TableCell>
                          {destination.address.name ? (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{destination.address.name}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span>{formatAddress(destination.address)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{formatLocation(destination.address)}</span>
                        </TableCell>
                        <TableCell>
                          {destination.location ? (
                            <Badge variant="secondary" className="font-mono text-xs">
                              {destination.location.lat.toFixed(2)}, {destination.location.lng.toFixed(2)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {new Date(destination.createdAt).toLocaleDateString()}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(page * PAGE_SIZE, allDestinations.length)} of{' '}
                    {allDestinations.length} destinations
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <DestinationFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreate}
        isProcessing={isProcessing}
      />
    </div>
  );
}
